import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, Pencil, Loader2, ImagePlus, X, Star, BadgeCheck } from "lucide-react";
import { PaymentTypesField } from "@/components/PaymentTypesField";

const TIERS = ["free", "premium"];

export function FacilitiesManager() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const search = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.get("/facilities", { params: { search: q || undefined, limit: 60 } });
      setResults(data.facilities || []);
    } catch { toast.error("Search failed"); } finally { setLoading(false); }
  };

  return (
    <div>
      <p className="text-[#4A534E] mb-4">Search and edit <b>any</b> facility — including pre-loaded ones. Change details, photos, availability, and switch plans.</p>
      <form onSubmit={search} className="flex gap-2 max-w-xl mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5C6660]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="fm-search" placeholder="Facility name, city, or county..." className="pl-9 rounded-xl h-11" />
        </div>
        <Button type="submit" className="bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl h-11" data-testid="fm-search-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}</Button>
      </form>

      {results.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-[#E8E6DF] text-[#5C6660]">Search to find facilities to edit.</div>
      ) : (
        <div className="space-y-2" data-testid="fm-results">
          {results.map((f) => (
            <div key={f.facility_id} className="bg-white border border-[#E8E6DF] rounded-xl p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium text-[#1C211F] truncate flex items-center gap-2">
                  {f.name}
                  {f.tier === "premium" && <Star className="h-3.5 w-3.5 text-[#0077B6] fill-[#0077B6]" />}
                  {f.claimed && <BadgeCheck className="h-3.5 w-3.5 text-[#0077B6]" />}
                </div>
                <div className="text-xs text-[#5C6660]">{f.city || f.county} · {f.facility_type} · plan: <b className="capitalize">{f.tier || "free"}</b></div>
              </div>
              <Button onClick={() => setEditing(f)} variant="outline" size="sm" className="rounded-xl border-[#E8E6DF] shrink-0" data-testid="fm-edit-btn"><Pencil className="h-4 w-4 mr-1.5" /> Edit</Button>
            </div>
          ))}
        </div>
      )}

      {editing && <EditDialog facility={editing} onClose={() => setEditing(null)} onSaved={(updated) => { setResults((r) => r.map((x) => x.facility_id === updated.facility_id ? updated : x)); setEditing(null); }} />}
    </div>
  );
}

function EditDialog({ facility, onClose, onSaved }) {
  const [f, setF] = useState(facility);
  const [form, setForm] = useState({
    description: facility.description || "",
    amenities: (facility.amenities || []).join(", "),
    photos: facility.photos || [],
    available_beds: facility.available_beds ?? "",
    contact_email: facility.contact_email || "",
    website: facility.website || "",
    licensed_beds_total: facility.licensed_beds_total ?? "",
    payment_types: facility.payment_types || [],
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tierLoading, setTierLoading] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const toggle = async (key, value) => {
    setF((prev) => ({ ...prev, [key]: value }));
    try {
      const { data } = await api.put(`/facilities/${f.facility_id}`, { [key]: value });
      setF(data); onSaved(data);
    } catch (e) {
      setF((prev) => ({ ...prev, [key]: !value }));
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  const switchTier = async (tier) => {
    setTierLoading(true);
    try {
      const { data } = await api.put(`/facilities/${f.facility_id}`, { tier });
      setF(data); onSaved(data);
      toast.success(`Plan set to ${tier}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change plan"); }
    finally { setTierLoading(false); }
  };

  const upload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); set("photos", [...form.photos, data.url]); }
    catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/facilities/${f.facility_id}`, {
        description: form.description,
        amenities: form.amenities.split(",").map((s) => s.trim()).filter(Boolean),
        photos: form.photos,
        available_beds: form.available_beds === "" ? null : parseInt(form.available_beds, 10),
        contact_email: form.contact_email || null,
        website: form.website || null,
        licensed_beds_total: form.licensed_beds_total === "" ? 0 : parseInt(form.licensed_beds_total, 10),
        payment_types: form.payment_types,
      });
      toast.success("Facility updated");
      onSaved(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="fm-edit-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{f.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Plan / tier</Label>
            <div className="flex gap-2" data-testid="tier-switch">
              {TIERS.map((t) => (
                <button key={t} type="button" disabled={tierLoading} onClick={() => switchTier(t)} data-testid={`tier-${t}`}
                  className={`flex-1 h-10 rounded-xl border text-sm font-medium capitalize transition-colors ${f.tier === t ? "bg-[#1A365D] text-white border-[#1A365D]" : "bg-white text-[#2A312D] border-[#E8E6DF] hover:border-[#1A365D]"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2 border-t border-[#E8E6DF] pt-4" data-testid="facility-toggles">
            <Label>Visibility & features</Label>
            <ToggleRow label="Published (visible to public)" desc="Hide to unpublish this listing." checked={f.published !== false} onChange={(v) => toggle("published", v)} testid="toggle-published" />
            <ToggleRow label="Featured (force top ranking)" desc="Pin above tier ordering in results." checked={!!f.featured} onChange={(v) => toggle("featured", v)} testid="toggle-featured" />
            <ToggleRow label="Inquiries enabled" desc="Show the inquiry form to families. Defaults ON for Premium, OFF otherwise." checked={f.inquiries_enabled !== undefined && f.inquiries_enabled !== null ? !!f.inquiries_enabled : f.tier === "premium"} onChange={(v) => toggle("inquiries_enabled", v)} testid="toggle-inquiries" />
            <ToggleRow label="Direct inquiries to facility" desc="Route inquiries to this facility's email (needs inquiry email set)." checked={!!f.direct_inquiries} onChange={(v) => toggle("direct_inquiries", v)} testid="toggle-direct" />
            <ToggleRow label="SMS bed updates" desc="Allow Twilio bed-count update texts for this facility." checked={f.sms_enabled !== false} onChange={(v) => toggle("sms_enabled", v)} testid="toggle-sms" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label htmlFor="fm-beds">Beds available</Label><Input id="fm-beds" type="number" min="0" data-testid="fm-beds" value={form.available_beds} onChange={(e) => set("available_beds", e.target.value)} className="rounded-xl" /></div>
            <div className="space-y-1.5"><Label htmlFor="fm-licensed">Licensed beds</Label><Input id="fm-licensed" type="number" min="0" data-testid="fm-licensed" value={form.licensed_beds_total} onChange={(e) => set("licensed_beds_total", e.target.value)} className="rounded-xl" /></div>
          </div>
          <div className="space-y-1.5"><Label htmlFor="fm-contact">Inquiry email</Label><Input id="fm-contact" type="email" data-testid="fm-contact" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="rounded-xl" /></div>
          <div className="space-y-1.5"><Label htmlFor="fm-website">Website (shown on profile for Premium)</Label><Input id="fm-website" data-testid="fm-website" value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://facility.com" className="rounded-xl" /></div>
          <div className="space-y-1.5"><Label htmlFor="fm-desc">Description</Label><Textarea id="fm-desc" rows={3} data-testid="fm-desc" value={form.description} onChange={(e) => set("description", e.target.value)} className="rounded-xl" /></div>
          <div className="space-y-1.5"><Label htmlFor="fm-amenities">Amenities (comma separated)</Label><Input id="fm-amenities" data-testid="fm-amenities" value={form.amenities} onChange={(e) => set("amenities", e.target.value)} className="rounded-xl" /></div>
          <PaymentTypesField value={form.payment_types} onChange={(v) => set("payment_types", v)} />
          <div className="space-y-2">
            <Label>Photos</Label>
            <div className="flex flex-wrap gap-2">
              {form.photos.map((p, i) => (
                <div key={p} className="relative"><img src={p} alt={`Facility photo ${i + 1}`} className="h-16 w-16 rounded-xl object-cover" />
                  <button type="button" onClick={() => set("photos", form.photos.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 bg-white rounded-full border border-[#E8E6DF] p-0.5"><X className="h-3 w-3" /></button>
                </div>
              ))}
              <label className="h-16 w-16 rounded-xl border-2 border-dashed border-[#E8E6DF] flex items-center justify-center cursor-pointer hover:border-[#1A365D]" data-testid="fm-photo">
                {uploading ? <Loader2 className="h-5 w-5 animate-spin text-[#5C6660]" /> : <ImagePlus className="h-5 w-5 text-[#5C6660]" />}
                <input type="file" accept="image/*" className="hidden" onChange={upload} />
              </label>
            </div>
          </div>
          <Button onClick={save} disabled={saving} className="w-full bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl h-11" data-testid="fm-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function ToggleRow({ label, desc, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <div className="min-w-0">
        <div className="text-sm font-medium text-[#1C211F]">{label}</div>
        <div className="text-xs text-[#5C6660]">{desc}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
